/**
 * Owner: Haselton Baker Risk Group, LLC
 * Copyright All Rights Reserved
 */
// @flow
import type { Element } from 'react';

import React, { Fragment } from 'react';
import keyBy from 'lodash/fp/keyBy.js';
import { Button, Table } from 'reactstrap';
import {
  FLOOR_PLAN_BY_STORY,
  FLOOR_PLAN_BY_STORY_FT_DIR_1,
  FLOOR_PLAN_BY_STORY_FT_DIR_2,
  FLOOR_PLAN_BY_STORY_LOCATION_SPECIFIER,
} from '@hbrisk/sp3-risk-model-support/models/app/attributes/names/index.js';
import { normalizeLocationSpecifierTokens } from '@hbrisk/sp3-risk-model-support/models/locationSpecifier/index.js';
import Field from '#components/pages/Models/Model/sections/support/forms/Field/index.js';
import { FieldFeedback, RenderInput } from '#components/support/forms/utility/index.js';
import Tip from '#components/support/tooltips/Tip/index.jsx';

type Props = {|
  form: string,
  fieldsByName: Object,
  fields: {
    length: number,
    map: Function,
    push: Function,
    remove: Function,
  },
  meta: { error: string },
|};
const renderFloorPlanByStory = ({
  form,
  fieldsByName: parentFieldsByName,
  fields,
  meta,
}: Props): Element<typeof Fragment> => {
  const { error } = meta;
  const itemSchemaByName = keyBy('name', parentFieldsByName[FLOOR_PLAN_BY_STORY].itemSchema);
  const locationField = itemSchemaByName[FLOOR_PLAN_BY_STORY_LOCATION_SPECIFIER];
  const ftDir1Field = itemSchemaByName[FLOOR_PLAN_BY_STORY_FT_DIR_1];
  const ftDir2Field = itemSchemaByName[FLOOR_PLAN_BY_STORY_FT_DIR_2];
  const allowDelete = fields.length > 1 || false;
  const floorPlanByStoryRows = fields.map((field, index) => (
    <tr key={field}>
      <td>
        <Field
          form={form}
          id={`${field}.${locationField.name}`}
          name={`${field}.${locationField.name}`}
          type={locationField.type}
          component={RenderInput}
          format={normalizeLocationSpecifierTokens}
          normalize={normalizeLocationSpecifierTokens}
        />
        <FieldFeedback name={`${field}.${locationField.name}`} form={form} />
      </td>
      <td>
        <Field
          form={form}
          id={`${field}.${ftDir1Field.name}`}
          name={`${field}.${ftDir1Field.name}`}
          type={ftDir1Field.type}
          component={RenderInput}
        />
        <FieldFeedback name={`${field}.${ftDir1Field.name}`} form={form} />
      </td>
      <td>
        <Field
          form={form}
          id={`${field}.${ftDir2Field.name}`}
          name={`${field}.${ftDir2Field.name}`}
          type={ftDir2Field.type}
          component={RenderInput}
        />
        <FieldFeedback name={`${field}.${ftDir2Field.name}`} form={form} />
      </td>
      <td>
        <Button color="link" onClick={() => fields.remove(index)} disabled={!allowDelete}>
          delete
        </Button>
      </td>
    </tr>
  ));

  return (
    <>
      <Table responsive className="floor-plan-by-story-table">
        <thead>
          <tr>
            <th>
              Stories <Tip tipId="story" />
            </th>
            <th>Direction 1</th>
            <th>Direction 2</th>
          </tr>
        </thead>
        <tbody>{floorPlanByStoryRows}</tbody>
      </Table>
      {error && <div className="text-danger">{error}</div>}
      <Button
        size="sm"
        id="addFloorPlanRowButton"
        className="float-right"
        color="primary"
        onClick={() => fields.push({})}
      >
        Add Row
      </Button>
    </>
  );
};

export default renderFloorPlanByStory;
